/**
 * src/hooks/useFocusTrap.ts
 * =============================================================================
 * Trampa de foco reutilizable para modales, sin librerías.
 *
 * Qué hace mientras el modal está montado/abierto:
 *  1. Recuerda qué elemento tenía el foco (el botón que abrió el modal).
 *  2. Mueve el foco dentro del contenedor: al elemento con `data-autofocus`
 *     si existe, o al primer enfocable en orden de documento.
 *  3. Captura Tab / Shift+Tab y cicla dentro del contenedor — el foco nunca
 *     se escapa al fondo tapado por el backdrop.
 *  4. Al cerrar, devuelve el foco al disparador (si sigue en el DOM).
 *
 * Modales anidados (drawer → CreateSpaceModal, etc.): se mantiene una PILA
 * global de trampas activas y solo la de más arriba gestiona Tab. Al cerrarse
 * la interior, la exterior recupera el mando sin re-registrar nada.
 *
 * No gestiona Escape ni click-fuera: cada modal ya tiene su propio manejo y
 * duplicarlo aquí acoplaría cierre y foco, que son responsabilidades distintas.
 * =============================================================================
 */

import { useEffect, type RefObject } from 'react'

/** Selector de elementos potencialmente enfocables con teclado. */
const ENFOCABLES =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** Pila de trampas activas — la última montada es la que gestiona Tab. */
const pilaDeTrampas: HTMLElement[] = []

/** ¿El elemento se está pintando? Filtra `display:none` (input file oculto,
 *  secciones colapsadas) sin depender de `offsetParent`, que es null también
 *  para posicionados `fixed`. */
function esVisible(el: HTMLElement): boolean {
  return el.getClientRects().length > 0
}

function enfocablesDe(contenedor: HTMLElement): HTMLElement[] {
  return Array.from(contenedor.querySelectorAll<HTMLElement>(ENFOCABLES)).filter(esVisible)
}

/**
 * @param ref     Contenedor del modal (el card o el wrapper con role="dialog").
 * @param activo  `true` mientras el modal está abierto. Para modales que se
 *                montan/desmontan enteros basta el valor por defecto.
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, activo = true) {
  useEffect(() => {
    if (!activo) return undefined
    const contenedor = ref.current
    if (!contenedor) return undefined

    // El disparador se captura ANTES de mover el foco: es a quien volver.
    const disparador =
      document.activeElement instanceof HTMLElement ? document.activeElement : null

    pilaDeTrampas.push(contenedor)

    // Foco inicial: `data-autofocus` manda (el input de email de un form de
    // invitar, no la X de cerrar); si no hay, el primer enfocable.
    const preferido = contenedor.querySelector<HTMLElement>('[data-autofocus]')
    const inicial =
      (preferido && esVisible(preferido) ? preferido : null) ?? enfocablesDe(contenedor)[0]
    inicial?.focus()

    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return
      // Solo la trampa de más arriba de la pila cicla — la de un modal
      // anidado gana a la del drawer que lo abrió.
      if (pilaDeTrampas[pilaDeTrampas.length - 1] !== contenedor) return
      const lista = enfocablesDe(contenedor)
      if (lista.length === 0) {
        e.preventDefault()
        return
      }
      const primero = lista[0]
      const ultimo = lista[lista.length - 1]
      const activoAhora = document.activeElement
      const dentro = activoAhora instanceof Node && contenedor.contains(activoAhora)
      if (e.shiftKey) {
        // Shift+Tab desde el primero (o desde fuera) → salta al último.
        if (!dentro || activoAhora === primero) {
          e.preventDefault()
          ultimo?.focus()
        }
      } else if (!dentro || activoAhora === ultimo) {
        // Tab desde el último (o desde fuera) → vuelve al primero.
        e.preventDefault()
        primero?.focus()
      }
    }

    // Fase de captura: gana a cualquier listener del fondo y funciona aunque
    // el foco esté en el propio body.
    document.addEventListener('keydown', onKeyDown, true)

    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      const i = pilaDeTrampas.lastIndexOf(contenedor)
      if (i >= 0) pilaDeTrampas.splice(i, 1)
      // Devolver el foco al disparador solo si sigue vivo en el DOM — puede
      // haberse desmontado (p. ej. un menuitem de un dropdown ya cerrado).
      if (disparador?.isConnected) disparador.focus()
    }
  }, [ref, activo])
}
